import React, { useEffect, useRef, useState } from 'react';
import BinIcon from '../components/icons/BinIcon';
import UsersIcon from '../components/icons/UsersIcon';
import SettingsIcon from '../components/icons/SettingsIcon';
import FileIcon from '../components/icons/FileIcon';
import { useI18n } from '../i18n/I18nProvider';
import authService from '../services/authService';

// NOTE: Screenshot placeholders
// Add PNG/JPG screenshots to `server-app/src/frontend/public/docs/<lang>/` folders.
// Example files:
//  - bins_overview.png, bins_add.png, bins_edit.png, bins_delete.png, bins_order.png, bins_collection_list.png
//  - sites_overview.png, sites_add.png, sites_edit.png
//  - files_overview.png, files_upload.png, files_assign.png
//  - users_overview.png, users_add.png, users_edit.png, users_delete.png
//  - settings_overview.png, settings_thresholds.png
// Reference in code via <img src={`/docs/${lang}/bins_overview.png`} ... />.

const Section = ({ id, title, children }) => (
  <section id={id} className="docs-section" style={{ marginBottom: 24, scrollMarginTop: 'calc(var(--header-height) + var(--docs-toc-height, 56px) + 24px)' }}>
    <h3 className="docs-section-title" style={{ margin: '8px 0 6px' }}>{title}</h3>
    <div style={{ fontSize: 14, lineHeight: 1.6 }}>{children}</div>
  </section>
);

export default function DocsPage(){
  const { t, lang } = useI18n();
  const currentUser = authService.getCurrentUser();
  const role = currentUser?.role || 'user';
  const roleLevel = { user: 1, manager: 2, admin: 3, superadmin: 4 }[role] || 1;
  const [activeId, setActiveId] = useState('altalanos');
  const pageRef = useRef(null);
  const suppressUntil = useRef(0);

  const Img = ({ name, alt }) => (
    // Screenshot placeholder usage (ensure file exists under /public/docs/<lang>/)
    <img src={`/docs/${lang}/${name}`} alt={alt} style={{ maxWidth: '100%', border: '1px solid #ddd', borderRadius: 6, margin: '8px 0' }} />
  );

  // Local copies of icons used in Sidebar (to keep iconography consistent)
  const SiteIcon = ({ size = 16 }) => (
    <svg width={size} height={size} viewBox="0 0 48 48" stroke="currentColor" fill="none" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M4 20 24 8l20 12v18c0 2.4-1.9 4.4-4.2 4.4H8.2C5.9 42.4 4 40.4 4 38Z" fill="currentColor" fillOpacity="0.10" />
      <path d="M4 20 24 8l20 12" />
      <path d="M12 42V26h24v16" />
      <rect x="20" y="30" width="8" height="8" rx="1.2" fill="currentColor" fillOpacity="0.22" />
    </svg>
  );
  const LogsIcon = ({ size = 16 }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <rect x="3" y="3" width="18" height="14" rx="2" ry="2" />
      <line x1="8" y1="21" x2="16" y2="21" />
      <line x1="12" y1="17" x2="12" y2="21" />
    </svg>
  );

  useEffect(() => {
    const ids = [
      'altalanos',
      'kukak',
      'telephelyek',
      ...(roleLevel >= 3 ? ['fajlok'] : []),
      'felhasznalok',
      ...(roleLevel >= 2 ? ['beallitasok'] : []),
      ...(roleLevel >= 3 ? ['logok'] : [])
    ];
    const onScroll = () => {
      if (Date.now() < suppressUntil.current) return;
      const header = document.querySelector('.app-header');
      const toc = document.querySelector('.docs-toc-bar');
      const headerH = header ? header.getBoundingClientRect().height : 64;
      const tocH = toc ? toc.getBoundingClientRect().height : 56;
      const offset = headerH + tocH + 1;
      const positions = ids.map(id => {
        const el = document.getElementById(id);
        if (!el) return { id, top: Number.POSITIVE_INFINITY, d: Number.POSITIVE_INFINITY };
        const top = el.getBoundingClientRect().top;
        return { id, top, d: top - offset };
      });
      const below = positions.filter(p => p.d >= 0).sort((a,b) => a.d - b.d);
      if (below.length) {
        setActiveId(below[0].id);
      } else {
        const above = positions.filter(p => p.d < 0).sort((a,b) => b.top - a.top);
        if (above[0]) setActiveId(above[0].id);
      }
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener('scroll', onScroll);
  }, [role]);

  // Track TOC height and expose as CSS var for precise anchor offset
  useEffect(() => {
    const applyTocHeight = () => {
      const toc = document.querySelector('.docs-toc-bar');
      const h = toc ? toc.getBoundingClientRect().height : 56;
      if (pageRef.current) pageRef.current.style.setProperty('--docs-toc-height', `${Math.ceil(h)}px`);
    };
    applyTocHeight();
    const ro = new ResizeObserver(applyTocHeight);
    const tocEl = document.querySelector('.docs-toc-bar');
    if (tocEl) ro.observe(tocEl);
    window.addEventListener('resize', applyTocHeight);
    return () => { window.removeEventListener('resize', applyTocHeight); ro.disconnect(); };
  }, []);

  const scrollToTop = (e) => {
    e?.preventDefault?.();
    const header = document.querySelector('.app-header');
    const toc = document.querySelector('.docs-toc-bar');
    const headerH = header ? header.getBoundingClientRect().height : 64;
    const tocH = toc ? toc.getBoundingClientRect().height : 56;
    const target = document.getElementById('altalanos');
    if (target) {
      const top = window.scrollY + target.getBoundingClientRect().top - (headerH + tocH + 1);
      suppressUntil.current = Date.now() + 700;
      window.scrollTo({ top, behavior: 'smooth' });
    } else {
      suppressUntil.current = Date.now() + 700;
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
    setActiveId('altalanos');
  };

  const scrollToId = (id, e) => {
    e?.preventDefault?.();
    const header = document.querySelector('.app-header');
    const toc = document.querySelector('.docs-toc-bar');
    const headerH = header ? header.getBoundingClientRect().height : 64;
    const tocH = toc ? toc.getBoundingClientRect().height : 56;
    const target = document.getElementById(id);
    if (target) {
      const top = window.scrollY + target.getBoundingClientRect().top - (headerH + tocH + 1);
      suppressUntil.current = Date.now() + 700;
      window.scrollTo({ top, behavior: 'smooth' });
      setActiveId(id);
    }
  };

  return (
    <div ref={pageRef} className="sb-page">
      <h2 style={{ marginTop: 0 }}>{t('docs.title') || 'Dokumentáció'}</h2>
      <p style={{ fontSize: 14, opacity: 0.85 }}>{t('docs.intro') || 'Az alkalmazás áttekintése és működése szerepkörök szerint.'}</p>

      {/* Általános tudnivalók */}
      <Section id="altalanos" title={t('docs.general.title') || 'Általános tudnivalók'}>
        <p>{t('docs.general.purpose')}</p>
        <p>{t('docs.general.structure')}</p>
        <ul>
          <li>{t('docs.general.features.mapList')}</li>
          {roleLevel >= 2 && <li>{t('docs.general.features.collection')}</li>}
          {roleLevel >= 3 && <li>{t('docs.general.features.media')}</li>}
          <li>{t('docs.general.features.sites')}</li>
          <li>{t('docs.general.features.users')}</li>
          {roleLevel >= 2 && <li>{t('docs.general.features.settings')}</li>}
          {roleLevel >= 3 && <li>{t('docs.general.features.logs')}</li>}
        </ul>
        <p>{t('docs.general.roles')}</p>
        <p>{t('docs.general.tips')}</p>
      </Section>

      {/* Könyvjelzős menü – szekció linkek */}
      <div className="docs-toc-bar">
        <ul className="docs-toc">
          <li><a onClick={(e)=>scrollToId('kukak', e)} className={activeId==='kukak'?'active':''} href="#kukak"><BinIcon size={16} /> {t('docs.bins.title') || 'Kukák'}</a></li>
          <li><a onClick={(e)=>scrollToId('telephelyek', e)} className={activeId==='telephelyek'?'active':''} href="#telephelyek"><SiteIcon /> {t('docs.sites.title') || 'Telephelyek'}</a></li>
          {(roleLevel >= 3) && (
            <li><a onClick={(e)=>scrollToId('fajlok', e)} className={activeId==='fajlok'?'active':''} href="#fajlok"><FileIcon size={16} /> {t('docs.files.title') || 'Fájlok'}</a></li>
          )}
          <li><a onClick={(e)=>scrollToId('felhasznalok', e)} className={activeId==='felhasznalok'?'active':''} href="#felhasznalok"><UsersIcon size={16} /> {t('docs.users.title') || 'Felhasználók'}</a></li>
          {(roleLevel >= 2) && (
            <li><a onClick={(e)=>scrollToId('beallitasok', e)} className={activeId==='beallitasok'?'active':''} href="#beallitasok"><SettingsIcon size={16} /> {t('docs.settings.title') || 'Beállítások'}</a></li>
          )}
          {roleLevel >= 3 && (
            <li><a onClick={(e)=>scrollToId('logok', e)} className={activeId==='logok'?'active':''} href="#logok"><LogsIcon /> {t('docs.logs.title') || 'Logok'}</a></li>
          )}
          <li className="docs-toc-spacer"></li>
          <li>
            <button className="docs-back-top-inline" onClick={scrollToTop} title={t('docs.backTop') || 'Vissza az elejére'}>
              ↑ {t('docs.backTop') || 'Vissza az elejére'}
            </button>
          </li>
        </ul>
      </div>

      {/* Szekció-legendák */}

      {/* Bins / Kukák – részletes lépések */}
      <Section id="kukak" title={t('docs.bins.title') || 'Kukák'}>
        <p>{t('docs.bins.overview') || 'A Kukák menüben megtekintheted a térképet, listát és a kiválasztott kuka részleteit.'}</p>
        <p>
          {t('docs.bins.steps1') || 'Ehhez: kattintson a bal oldali menüben a “Kukák” pontra. A felső sorban a “Térkép” és “Lista” nézetek között tud váltani. A térképen kattintson egy ikonra a részletek megnyitásához; a listában kattintson a sorra.'}
        </p>
        <Img name="bins_overview.png" alt="Bins overview" />
        <div className="docs-legend">
          <div>
            <strong>{t('docs.legend.fill.title') || 'Telítettség'}</strong>
            <div className="legend-row">
              <span className="legend-badge legend-fill-green">{t('docs.legend.fill.green') || 'Zöld'}</span>
              <span className="legend-badge legend-fill-yellow">{t('docs.legend.fill.yellow') || 'Sárga'}</span>
              <span className="legend-badge legend-fill-red">{t('docs.legend.fill.red') || 'Piros'}</span>
              <span className="legend-badge legend-fill-none">{t('docs.legend.fill.none') || 'Nincs adat'}</span>
            </div>
          </div>
          <div>
            <strong>{t('docs.legend.battery.title') || 'Akkumulátor'}</strong>
            <div className="legend-row">
              <span className="legend-badge legend-battery-ok">🔋 {t('docs.legend.battery.ok') || 'Rendben'}</span>
              <span className="legend-badge legend-battery-low">🔋 {t('docs.legend.battery.low') || 'Alacsony'}</span>
            </div>
          </div>
          <div>
            <strong>{t('docs.legend.lock.title') || 'Zár'}</strong>
            <div className="legend-row">
              <span className="legend-badge legend-lock-closed">🔒 {t('docs.legend.lock.closed') || 'Zárva'}</span>
              <span className="legend-badge legend-lock-open">🔓 {t('docs.legend.lock.open') || 'Nyitva'}</span>
            </div>
          </div>
          <div>
            <strong>{t('docs.legend.signal.title') || 'Jel'}</strong>
            <div className="legend-row">
              <span className="legend-badge legend-signal-excellent">📶 {t('docs.legend.signal.excellent') || 'Kiváló'}</span>
              <span className="legend-badge legend-signal-good">📶 {t('docs.legend.signal.good') || 'Jó'}</span>
              <span className="legend-badge legend-signal-weak">📶 {t('docs.legend.signal.weak') || 'Gyenge'}</span>
            </div>
          </div>
        </div>
        <p style={{ marginTop: 8 }}>
          <strong>Jel (térerő) piktogram:</strong> a lista „Jel” oszlopában és a tooltipben színes piktogram jelzi a kapcsolat minőségét
          (<span style={{ color:'#16a34a', fontWeight:600 }}>Kiváló</span>, <span style={{ color:'#f59e0b', fontWeight:600 }}>Jó</span>, <span style={{ color:'#ef4444', fontWeight:600 }}>Gyenge</span>).
        </p>
        <ul>
          <li>{t('docs.bins.map') || 'Térkép: színezett ikonok (zöld/sárga/piros), akkumulátor és zár állapot jelzések.'}</li>
          <li>{t('docs.bins.list') || 'Lista: telítettség progress bar, akkumulátor feszültség, zár állapot, rendezés.'}</li>
          <li>{t('docs.bins.details') || 'Részletek: mini térkép, állapotkártyák, grafikonok, előzmények.'}</li>
        </ul>
        {(roleLevel >= 2) && (
          <>
            <p>{t('docs.bins.manage') || 'Kuka menedzsment: hozzáadás, módosítás, törlés, sorrend beállítása, ürítési lista generálása.'}</p>
            <p>
              {t('docs.bins.steps2') || 'Új kuka hozzáadásához kattintson a jobb felső “+ Új kuka” gombra; töltse ki a mezőket, majd kattintson a “Mentés” gombra. Módosításhoz nyissa meg a kuka részleteit és kattintson a “Szerkesztés” gombra; törléshez a kuka sorában kattintson a “Törlés” ikonra.'}
            </p>
            <Img name="bins_add.png" alt="Add bin" />
            <Img name="bins_edit.png" alt="Edit bin" />
            <Img name="bins_delete.png" alt="Delete bin" />
            <p>{t('docs.bins.order') || 'Sorrend beállításához nyissa meg a listát és használja a “Fel/Le” vezérlőket vagy húzza a sorokat; a változások mentéséhez kattintson a “Mentés sorrend” gombra.'}</p>
            <Img name="bins_order.png" alt="Order bins" />
            <p>{t('docs.bins.collection') || 'Ürítési lista generálásához válassza ki a szűrőt (telephely, telítettség), majd kattintson a “Lista generálása” gombra. A kapott listát PDF-be vagy CSV-be exportálhatja.'}</p>
            <Img name="bins_collection_list.png" alt="Collection list" />
          </>
        )}
        {role === 'user' && (
          <p>{t('docs.bins.userView') || 'Felhasználóként megtekintheted a térképet és listát, a részletek között a telítettség és állapot információkat.'}</p>
        )}
      </Section>

      {/* Sites / Telephelyek – részletes lépések */}
      <Section id="telephelyek" title={t('docs.sites.title') || 'Telephelyek'}>
        <p>{t('docs.sites.overview') || 'Telephelyek kezelése és áttekintése.'}</p>
        <p>{t('docs.sites.steps') || 'Ehhez: kattintson a bal oldali “Telephelyek” menüre. Új telephely: “+ Új telephely”. Módosítás: telephely sorának “Szerkesztés”. Mentés után azonnal frissül a térkép és lista.'}</p>
        <Img name="sites_overview.png" alt="Sites overview" />
                <div className="docs-legend">
                  <div>
                    <strong>{t('docs.legend.sites.title') || 'Telephelyek'}</strong>
                    <div className="legend-row">
                      <span className="legend-badge">🏷️ {t('docs.legend.sites.code') || 'Kód'}</span>
                      <span className="legend-badge">📍 {t('docs.legend.sites.location') || 'Pozíció'}</span>
                    </div>
                  </div>
                </div>
        {(roleLevel >= 2) && (
          <>
            <Img name="sites_add.png" alt="Add site" />
            <Img name="sites_edit.png" alt="Edit site" />
          </>
        )}
      </Section>

      {/* Files / Fájlok – részletes lépések (Admin+) */}
      {(roleLevel >= 3) && (
      <Section id="fajlok" title={t('docs.files.title') || 'Fájlok'}>
        <p>{t('docs.files.overview') || 'Hang- és képfájlok kezelése, hozzárendelés a kukákhoz.'}</p>
        <p>{t('docs.files.steps') || 'Ehhez: kattintson a “Fájlok” menüre. Feltöltéshez húzza be a fájlt vagy kattintson a “Feltöltés” gombra. Hozzárendeléshez válassza ki a kukát és kattintson a “Hozzárendelés” gombra a kiválasztott fájlnál.'}</p>
        <Img name="files_overview.png" alt="Files overview" />
        <div className="docs-legend">
          <div>
            <strong>{t('docs.legend.media.title') || 'Média'}</strong>
            <div className="legend-row">
              <span className="legend-badge">🎵 {t('docs.legend.media.audio') || 'Hang'}</span>
              <span className="legend-badge">🖼️ {t('docs.legend.media.image') || 'Kép'}</span>
            </div>
          </div>
        </div>
        <>
          <Img name="files_upload.png" alt="Upload files" />
          <Img name="files_assign.png" alt="Assign files" />
        </>
      </Section>
      )}

      {/* Users / Felhasználók – részletes lépések */}
      <Section id="felhasznalok" title={t('docs.users.title') || 'Felhasználók'}>
        <p>{t('docs.users.overview') || 'Felhasználók kezelése: létrehozás, módosítás, törlés, jelszó csere.'}</p>
        {roleLevel >= 2 ? (
          <>
            <p>{t('docs.users.steps') || 'Ehhez: kattintson a “Felhasználók” menüre. Új felhasználó: “+ Új felhasználó”. Jelszó mező egy sorban marad; töltse ki az adatokat, majd “Mentés”. Szerkesztéshez nyissa meg a sor melletti “Szerkesztés” ikont; a szerepkört a lenyílóból állítsa.'}</p>
            <Img name="users_overview.png" alt="Users overview" />
            <Img name="users_add.png" alt="Add user" />
            <Img name="users_edit.png" alt="Edit user" />
            <Img name="users_delete.png" alt="Delete user" />
          </>
        ) : (
          <>
            <p>{t('docs.users.readOnly') || 'Felhasználóként csak olvasási jogosultsággal rendelkezik: megtekintheti a listát és részleteket, de nem hozhat létre, nem módosíthat és nem törölhet felhasználót.'}</p>
            <Img name="users_overview.png" alt="Users overview" />
          </>
        )}
        <div className="docs-legend">
          <div>
            <strong>{t('docs.legend.roles.title') || 'Szerepkörök'}</strong>
            <div className="legend-row">
              {roleLevel >= 4 && <span className="legend-badge">⭐ {t('docs.legend.roles.superadmin') || 'Szuperadmin'}</span>}
              {roleLevel >= 3 && <span className="legend-badge">🛠️ {t('docs.legend.roles.admin') || 'Admin'}</span>}
              {roleLevel >= 2 && <span className="legend-badge">📋 {t('docs.legend.roles.manager') || 'Menedzser'}</span>}
              <span className="legend-badge">👤 {t('docs.legend.roles.user') || 'Felhasználó'}</span>
            </div>
          </div>
        </div>
      </Section>

      {/* Settings / Beállítások – részletes lépések */}
      {(roleLevel >= 2) && (
        <Section id="beallitasok" title={t('docs.settings.title') || 'Beállítások'}>
          <p>{t('docs.settings.overview') || 'Globális beállítások: nyelv, telemetriai küszöbök (akkumulátor, telítettség), audit naplózás.'}</p>
          <p>{t('docs.settings.steps') || 'Ehhez: kattintson a “Beállítások” menüre. A “Telemetria” szekcióban állítsa be az Akkumulátor alsó küszöböt (mV) és a Telítettség piros/alsó valamint zöld/felső küszöböt (%). Mentéshez kattintson a “Mentés” gombra; a térkép és a lista színezése azonnal frissül.'}</p>
          <p>{t('docs.settings.tips') || 'Tipp: az információs ikonokra (i) húzva az egeret megjelennek a részletes magyarázatok. Ha a zöld felső nem nagyobb a piros alsónál, a Mentés gomb letiltható.'}</p>
          <Img name="settings_overview.png" alt="Settings overview" />
          <Img name="settings_thresholds.png" alt="Settings thresholds" />
          <div className="docs-legend">
            <div>
              <strong>{t('docs.legend.fill.title') || 'Telítettség'}</strong>
              <div className="legend-row">
                <span className="legend-badge legend-fill-green">{t('docs.legend.fill.green') || 'Zöld'}</span>
                <span className="legend-badge legend-fill-yellow">{t('docs.legend.fill.yellow') || 'Sárga'}</span>
                <span className="legend-badge legend-fill-red">{t('docs.legend.fill.red') || 'Piros'}</span>
              </div>
            </div>
            <div>
              <strong>{t('docs.legend.battery.title') || 'Akkumulátor'}</strong>
              <div className="legend-row">
                <span className="legend-badge legend-battery-ok">🔋 {t('docs.legend.battery.ok') || 'Rendben'}</span>
                <span className="legend-badge legend-battery-low">🔋 {t('docs.legend.battery.low') || 'Alacsony'}</span>
              </div>
            </div>
          </div>
        </Section>
      )}

      {/* Admin extra */}
      {roleLevel >= 3 && (
        <Section id="admin" title={t('docs.admin.title') || 'Admin funkciók'}>
          <p>{t('docs.admin.overview') || 'Audit logok, jogosultságkezelés, rendszerkonfiguráció és speciális beállítások.'}</p>
        </Section>
      )}

      {/* Logok – audit és események */}
      {roleLevel >= 3 && (
      <Section id="logok" title={t('docs.logs.title') || 'Logok'}>
        <p>
          {t('docs.logs.overview') || 'A Logok menü az audit naplókat és eseményeket mutatja. Szűrhet időintervallum, entitás és művelet szerint, a részletekben pedig látható a változások előtte/utána állapota.'}
        </p>
        <p>
          {t('docs.logs.mediaAck') || 'A média (kép/hang) küldések visszaigazolásai (ACK) külön jelöléssel jelennek meg, azonnal frissülnek realtime eseményekkel.'}
        </p>
        <Img name="logs_overview.png" alt="Logs overview" />
        <div className="docs-legend">
          <div>
            <strong>{t('docs.legend.mediaAck.title') || 'Média visszaigazolás (ACK)'}</strong>
            <div className="legend-row">
              <span className="legend-badge legend-ack-ok">✅ {t('docs.legend.mediaAck.ok') || 'OK'}</span>
              <span className="legend-badge legend-ack-fail">❌ {t('docs.legend.mediaAck.fail') || 'Hiba'}</span>
            </div>
          </div>
        </div>
      </Section>
      )}

      {/* Floating button removed; inline button lives in TOC bar */}
    </div>
  );
}
